<script setup>
import CareerHeader from '@/Components/Content/Header/Career.vue';
import AccordionGroup from '@/Components/Accordion/Group.vue';
import AccordionItem from '@/Components/Accordion/Item.vue';
import Default from '@/Layouts/Default.vue';
import Content from '@/Components/Content/index.vue';
import { Head, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps({
  career: {
    type: Object,
    required: true,
  },
});

const page = usePage();
const context = computed(() => page.props?.context);
const providerName = context.value.name;
</script>

<template>
  <Head>
    <title>{{ props.career.name }} - {{ providerName }}</title>
    <meta name="description" :content="props.career.description">
  </Head>
  <Default>
    <Content>
      <template #header>
        <CareerHeader
          page="career"
          :career="career"
        />
      </template>
      <template #content>
        <p class="my-6">
          {{ career.description }}
        </p>

        <p class="mb-3"><strong>What the job involves</strong></p>
        <ul class="list-disc ps-6">
                <li v-for="task, index in career.tasks" :key="`career-task-${index}`" class="mb-3">
                  <p>{{ task }}</p>
                </li>
              </ul>
        <AccordionGroup>
          <AccordionItem>
            <template #title>
              Fields of Work
            </template>
            <template #content>
              <ul class="list-disc">
                <li v-for="industry, index in career.industries" :key="`career-field-${index}`" class="mb-3">
                  <p>{{ industry.industry_name }}</p>
                </li>
              </ul>
            </template>
          </AccordionItem> 
          <AccordionItem>
            <template #title>
              The Facts
            </template>
            <template #content>
              <ul>
                <li class="mb-3">
                  <p><strong>Annual growth: </strong>{{ career.annual_employment_growth }} per year</p>
                </li>
                <li class="mb-3">
                  <p><strong>Average hours worked per week: </strong>{{ career.earnings_hours[0].average_full_time_hours_worked_per_week }}</p>
                </li>
                <li class="mb-3">
                  <p><strong>Median earning per week: </strong>${{ career.median_weekly_earnings }} AUD</p>
                </li>
                <li class="mb-3">
                  <p><strong>Share who work full time: </strong>{{ career.earnings_hours[0].share_of_workers_who_work_full_time_hours }}%</p>
                </li>
                <li class="mb-3">
                  <p><strong>Share of female workers: </strong> {{ career.female_share }}%</p>
                </li>
                <li class="mb-3">
                  <p><strong>Education level </strong></p>
                  <ul class="list-disc ps-4">
                    <li class="mb-2"><p><strong>Year 10 and below: </strong>{{career.education.year_10_and_below}}%</p></li>
                    <li class="mb-2"><p><strong>Year 11: </strong>{{career.education.year_11}}%</p></li>
                    <li class="mb-2"><p><strong>Year 12: </strong>{{career.education.year_12}}%</p></li>
                    <li class="mb-2"><p><strong>Certificate III or IV: </strong>{{career.education.certificate_iii_iv}}%</p></li>
                    <li class="mb-2"><p><strong>Advanced Diploma: </strong>{{career.education.advanced_diploma_diploma}}%</p></li>
                    <li class="mb-2"><p><strong>Bachelors Degree: </strong>{{career.education.bachelor_degree}}%</p></li>
                        <li class="mb-2"><p><strong>Post graduate, graduate diploma or graduate certificate: </strong>{{career.education.post_graduate_graduate_diploma_or_graduate_certificate}}%</p></li>
                  </ul>
                </li>
                <li>
                  <p><strong>Share around Australia</strong></p>

                  <ul class="list-disc ps-4">
                    <li class="mb-2"><p><strong>Victoria: </strong>{{career.states_and_territories.vic}}%</p></li>
                    <li class="mb-2"><p><strong>New South Wales: </strong>{{career.states_and_territories.nsw}}%</p></li>
                    <li class="mb-2"><p><strong>Australia Capital Territory: </strong>{{career.states_and_territories.act}}%</p></li>
                    <li class="mb-2"><p><strong>Northern Territory: </strong>{{career.states_and_territories.nt}}%</p></li>
                    <li class="mb-2"><p><strong>Tasmania: </strong>{{career.states_and_territories.tas}}%</p></li>
                    <li class="mb-2"><p><strong>Queensland: </strong>{{career.states_and_territories.qld}}%</p></li>
                    <li class="mb-2"><p><strong>Western Australia: </strong>{{career.states_and_territories.wa}}%</p></li>
                    <li class="mb-2"><p><strong>South Australia: </strong>{{career.states_and_territories.sa}}%</p></li>
                  </ul>
                </li>
              </ul>
            </template>
          </AccordionItem>
          <AccordionItem>
            <template #title>
              Values of workers in {{ career.name }}
            </template>
            <template #content>
              <ul>
                <li v-for="value, index in career.values" :key="`career-field-${index}`" class="mb-3">
                  <p><strong>{{ value.name }}</strong></p>
                  <p>{{ value.description }}</p>
                </li>
              </ul>
            </template>
          </AccordionItem>
          <AccordionItem>
            <template #title>
              Top skills required for {{ career.name }}
            </template>
            <template #content>
              <ul>
                <li v-for="skill, index in career.skills.slice(0, 5)" :key="`career-field-${index}`" class="mb-3">
                  <p><strong>{{ skill.name }}</strong></p>
                  <p>{{ skill.description }}</p>
                </li>
              </ul>
            </template>
          </AccordionItem>
        </AccordionGroup>
      </template>
    </Content>
  </Default>
</template>